import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  FileText,
  FolderOpen,
  Layers3,
  X,
} from "lucide-react";
import { useMobile } from "../../hooks/useMobile";
import { useWorkspace } from "../../context/WorkspaceContext";
import { getChildren, getTopLevelTexts, toSortedArray } from "../../services/workspaceService";
import { getRememberedWritingText } from "../../utils/writingSession";

function previewText(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/[`*_>#\[\]()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 110);
}

export default function EditorBinder() {
  const location = useLocation();
  const isMobile = useMobile();
  const { workspace } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [current, setCurrent] = useState(() => getRememberedWritingText());

  const isEditorRoute = location.pathname === "/editor" || location.pathname.startsWith("/editor/");

  const expandCurrentPath = useCallback((text = getRememberedWritingText()) => {
    if (!text) return;
    setExpanded((prev) => ({
      ...prev,
      ...(text.deskId ? { [`desk-${text.deskId}`]: true } : {}),
      ...(text.drawerId ? { [`drawer-${text.drawerId}`]: true } : {}),
      ...(text.notebookId ? { [`notebook-${text.notebookId}`]: true } : {}),
      ...(text.parentId ? { [`folder-${text.parentId}`]: true } : {}),
    }));
  }, []);

  useEffect(() => {
    const toggleBinder = () => {
      setOpen((wasOpen) => {
        if (!wasOpen) expandCurrentPath();
        return !wasOpen;
      });
    };
    window.addEventListener("trufapp:toggle-binder", toggleBinder);
    return () => window.removeEventListener("trufapp:toggle-binder", toggleBinder);
  }, [expandCurrentPath]);

  useEffect(() => {
    const sync = () => {
      const next = getRememberedWritingText();
      setCurrent(next);
      if (open) expandCurrentPath(next);
    };
    window.addEventListener("trufapp:writing-text", sync);
    return () => window.removeEventListener("trufapp:writing-text", sync);
  }, [expandCurrentPath, open]);

  useEffect(() => {
    if (!isEditorRoute) setOpen(false);
  }, [isEditorRoute]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);

  const desks = useMemo(() => workspace?.desks || [], [workspace]);

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const openText = (text, desk, drawer, notebook) => {
    const enriched = {
      ...text,
      deskId: desk.id,
      deskName: desk.name,
      deskSlug: desk.slug,
      drawerId: drawer.id,
      drawerName: drawer.name,
      drawerSlug: drawer.slug,
      notebookId: notebook.id,
      notebookName: notebook.name,
      notebookSlug: notebook.slug,
      notebookColor: notebook.color,
    };
    window.dispatchEvent(new CustomEvent("trufapp:open-writing-text", { detail: enriched }));
    setOpen(false);
  };

  if (!isEditorRoute || !open) return null;

  const renderText = (text, desk, drawer, notebook, allTexts, depth = 0) => {
    const isFolder = text.kind === "folder";
    const kids = isFolder ? getChildren(allTexts, text.id) : [];
    const folderKey = `folder-${text.id}`;
    const isExpanded = Boolean(expanded[folderKey]);
    const active = current?.id === text.id;
    const snippet = previewText(text.content);

    return (
      <div key={text.id}>
        <div
          style={{
            ...styles.textRow,
            paddingLeft: 8 + Math.min(depth, 3) * 12,
            background: active ? "rgba(212,175,55,0.13)" : "transparent",
          }}
        >
          {isFolder ? (
            <button
              type="button"
              onClick={() => toggle(folderKey)}
              style={styles.chevronButton}
              aria-label={isExpanded ? "Contraer carpeta" : "Expandir carpeta"}
            >
              {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
          ) : (
            <span style={styles.chevronSpacer} />
          )}

          <button
            type="button"
            onClick={() => (isFolder ? toggle(folderKey) : openText(text, desk, drawer, notebook))}
            style={styles.textButton}
          >
            <span style={styles.textIcon}>
              {isFolder ? <FolderOpen size={17} /> : <FileText size={17} />}
            </span>
            <span style={styles.textCopy}>
              <span style={{ ...styles.textTitle, color: active ? "#F9F6EE" : "#DDD5C8" }}>
                {text.title || "Sin título"}
              </span>
              {snippet && <span style={styles.snippet}>{snippet}</span>}
            </span>
          </button>
        </div>

        {isFolder && isExpanded && kids.map((child) => renderText(child, desk, drawer, notebook, allTexts, depth + 1))}
      </div>
    );
  };

  return (
    <>
      <div style={styles.backdrop} onClick={() => setOpen(false)} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Árbol de archivos"
        style={isMobile ? styles.mobileSheet : styles.desktopPanel}
      >
        {isMobile && <div style={styles.grabber} />}
        <div style={styles.header}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.eyebrow}>ARCHIVOS</div>
            <div style={styles.currentTitle}>{current?.title || "Manuscrito"}</div>
          </div>
          <button type="button" onClick={() => setOpen(false)} style={styles.closeButton} aria-label="Cerrar árbol">
            <X size={22} />
          </button>
        </div>

        <div style={styles.scrollArea}>
          {desks.map((desk) => {
            const deskKey = `desk-${desk.id}`;
            const deskOpen = Boolean(expanded[deskKey]);
            return (
              <div key={desk.id}>
                <button type="button" style={styles.groupRow} onClick={() => toggle(deskKey)}>
                  {deskOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <Briefcase size={17} />
                  <span style={styles.groupTitle}>{desk.name}</span>
                </button>

                {deskOpen && toSortedArray(desk.drawers).map((drawer) => {
                  const drawerKey = `drawer-${drawer.id}`;
                  const drawerOpen = Boolean(expanded[drawerKey]);
                  return (
                    <div key={drawer.id} style={styles.levelOne}>
                      <button type="button" style={styles.groupRow} onClick={() => toggle(drawerKey)}>
                        {drawerOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        <Layers3 size={17} />
                        <span style={styles.groupTitle}>{drawer.name}</span>
                      </button>

                      {drawerOpen && toSortedArray(drawer.notebooks).map((notebook) => {
                        const notebookKey = `notebook-${notebook.id}`;
                        const notebookOpen = Boolean(expanded[notebookKey]);
                        const texts = toSortedArray(notebook.texts);
                        return (
                          <div key={notebook.id} style={styles.levelTwo}>
                            <button type="button" style={styles.groupRow} onClick={() => toggle(notebookKey)}>
                              {notebookOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                              <BookOpen size={17} />
                              <span style={styles.groupTitle}>{notebook.name}</span>
                              <span style={styles.count}>{texts.length}</span>
                            </button>

                            {notebookOpen && (
                              <div style={styles.textList}>
                                {getTopLevelTexts(texts).map((text) => renderText(text, desk, drawer, notebook, texts))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {desks.length === 0 && <div style={styles.empty}>No hay archivos todavía.</div>}
        </div>
      </aside>
    </>
  );
}

const panelBase = {
  position: "fixed",
  zIndex: 1950,
  background: "rgba(24,20,17,0.97)",
  color: "#F9F6EE",
  border: "1px solid rgba(212,175,55,0.18)",
  boxShadow: "0 18px 60px rgba(0,0,0,0.5)",
  backdropFilter: "blur(18px)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1940,
    background: "rgba(0,0,0,0.34)",
  },
  mobileSheet: {
    ...panelBase,
    left: 0,
    right: 0,
    bottom: 0,
    height: "min(76dvh, 680px)",
    borderRadius: "20px 20px 0 0",
    paddingBottom: "env(safe-area-inset-bottom)",
  },
  desktopPanel: {
    ...panelBase,
    left: 0,
    top: 0,
    bottom: 0,
    width: "min(380px, 42vw)",
  },
  grabber: {
    width: 44,
    height: 5,
    borderRadius: 99,
    background: "rgba(255,255,255,0.22)",
    margin: "8px auto 2px",
    flexShrink: 0,
  },
  header: {
    minHeight: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "8px 10px 8px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    flexShrink: 0,
  },
  eyebrow: {
    color: "#D4AF37",
    fontSize: "0.65rem",
    fontWeight: 800,
    letterSpacing: "0.12em",
  },
  currentTitle: {
    marginTop: 2,
    maxWidth: "70vw",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    color: "#D8D0C4",
    fontSize: "0.78rem",
  },
  closeButton: {
    width: 48,
    height: 48,
    border: "none",
    borderRadius: 12,
    background: "rgba(255,255,255,0.06)",
    color: "#D4AF37",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    touchAction: "manipulation",
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-y",
    padding: "6px 8px calc(20px + env(safe-area-inset-bottom))",
  },
  groupRow: {
    width: "100%",
    minHeight: 48,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 8px",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    color: "#C8BDAE",
    textAlign: "left",
    touchAction: "manipulation",
  },
  groupTitle: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "0.88rem",
    fontWeight: 650,
  },
  count: {
    minWidth: 24,
    padding: "2px 6px",
    borderRadius: 99,
    background: "rgba(255,255,255,0.06)",
    color: "#81786E",
    fontSize: "0.68rem",
    textAlign: "center",
  },
  levelOne: { paddingLeft: 10 },
  levelTwo: { paddingLeft: 10 },
  textList: { paddingLeft: 2 },
  textRow: {
    minHeight: 56,
    display: "flex",
    alignItems: "stretch",
    borderRadius: 10,
  },
  chevronButton: {
    width: 44,
    minWidth: 44,
    minHeight: 48,
    border: "none",
    background: "transparent",
    color: "#A89F91",
    display: "grid",
    placeItems: "center",
    touchAction: "manipulation",
  },
  chevronSpacer: { width: 44, minWidth: 44 },
  textButton: {
    flex: 1,
    minWidth: 0,
    minHeight: 52,
    border: "none",
    background: "transparent",
    color: "inherit",
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "6px 8px 6px 0",
    textAlign: "left",
    touchAction: "manipulation",
  },
  textIcon: {
    width: 22,
    minWidth: 22,
    color: "#A89F91",
    display: "grid",
    placeItems: "center",
  },
  textCopy: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  textTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.88rem",
    fontWeight: 620,
  },
  snippet: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#776F66",
    fontSize: "0.72rem",
    lineHeight: 1.25,
  },
  empty: {
    padding: 24,
    textAlign: "center",
    color: "#776F66",
    fontSize: "0.82rem",
  },
};
